import type { ComponentType } from 'react'
import { Blocks, CalendarClock, Code2, CreditCard, Handshake, ShieldCheck, User, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useManagerNavigation } from '@/components/manager/useManagerNavigation'
import { TeamView } from '@/components/manager/TeamView'
import { ScheduleView } from '@/components/manager/ScheduleView'
import { TrustCenterView } from '@/components/manager/trust/TrustCenterView'
import { IntegrationsMarketplaceView } from '@/components/manager/IntegrationsMarketplaceView'
import { IntegrationsView } from '@/components/manager/IntegrationsView'
import { BillingView } from '@/components/manager/BillingView'
import { ProfilePage } from '@/components/dashboard/ProfilePage'
import { useAccess } from '@/hooks/useAccess'
import { PartnerSpace } from '@/components/partner/PartnerSpace'
import { usePartnerSpace } from '@/api/partenaires'

/**
 * Espace Réglages : une page à part avec sa propre colonne (modèle Quo).
 * On y va une fois, puis rarement ; la barre de gauche garde le quotidien.
 * Les vues affichées sont celles qui existent déjà ; les anciennes adresses
 * (?view=numbers, ?view=trust, ...) restent valides et tombent sur la bonne section.
 */
interface Section { id: string; icon: ComponentType<{ className?: string }>; label: string; View: ComponentType }

export const SETTINGS_SECTION_IDS = ['team', 'schedule', 'trust', 'integrations-marketplace', 'integrations', 'billing', 'profile', 'partner']
export const SETTINGS_DEFAULT_SECTION = 'team'

export function SettingsView({ section }: { section: string }) {
  const { t } = useTranslation()
  const { darkMode } = useUIStore()
  const { setActiveTab } = useManagerNavigation()
  const { canBilling } = useAccess()
  // The partner program: shown to a partner, or to everyone once the agreement is published.
  const partnerVisible = usePartnerSpace().data?.visible ?? false

  const groupes: { titre: string; sections: Section[] }[] = [
    { titre: t('manager.settings.company', 'Entreprise'), sections: [
      { id: 'team', icon: Users, label: t('manager.tabs.team', 'Équipe'), View: TeamView },
      { id: 'schedule', icon: CalendarClock, label: t('manager.tabs.schedule', 'Horaires'), View: ScheduleView },
      { id: 'trust', icon: ShieldCheck, label: t('manager.tabs.trust', 'Centre de confiance'), View: TrustCenterView },
      { id: 'integrations-marketplace', icon: Blocks, label: t('manager.tabs.integrations', 'Intégrations'), View: IntegrationsMarketplaceView },
      { id: 'integrations', icon: Code2, label: t('manager.tabs.developers', 'Développeurs'), View: IntegrationsView },
      // L'abonnement est au nom du propriétaire : lui seul le voit et le change.
      ...(canBilling ? [{ id: 'billing', icon: CreditCard, label: t('manager.tabs.billing', 'Facturation'), View: BillingView }] : []),
    ] },
    { titre: t('manager.settings.account', 'Mon compte'), sections: [
      { id: 'profile', icon: User, label: t('manager.tabs.profile', 'Profil'), View: ProfilePage },
      ...(partnerVisible ? [{ id: 'partner', icon: Handshake, label: t('partner.tab', 'Partenaire'), View: PartnerSpace }] : []),
    ] },
  ]
  const actif = groupes.flatMap((g) => g.sections).find((s) => s.id === section) ?? groupes[0].sections[0]
  const Vue = actif.View

  // Narrow window: the column becomes one scrollable row of sections above the page.
  return (
    <div className="h-full flex flex-col md:flex-row min-h-0">
      <aside className={cn(
        'flex-shrink-0 flex md:block gap-1 overflow-x-auto md:overflow-x-visible md:overflow-y-auto border-b md:border-b-0 md:border-r md:w-56 px-3 py-2 md:py-5 md:space-y-5',
        darkMode ? 'border-slate-800' : 'border-slate-200',
      )}>
        <h2 className={cn('hidden md:block px-2 text-base font-semibold', darkMode ? 'text-white' : 'text-slate-900')}>{t('manager.tabs.settings', 'Réglages')}</h2>
        {groupes.map((g) => (
          <div key={g.titre} className="contents md:block md:space-y-px">
            <p className={cn('hidden md:block px-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wider', darkMode ? 'text-slate-500' : 'text-slate-400')}>{g.titre}</p>
            {g.sections.map((s) => {
              const Icon = s.icon
              const estActif = s.id === actif.id
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveTab(s.id)}
                  className={cn(
                    'flex-shrink-0 md:w-full flex items-center gap-2.5 px-2 h-8 rounded-md text-[13px] whitespace-nowrap transition-colors',
                    estActif
                      ? darkMode ? 'bg-slate-800 text-white font-medium' : 'bg-slate-100 text-slate-900 font-medium'
                      : darkMode ? 'text-slate-300 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{s.label}</span>
                </button>
              )
            })}
          </div>
        ))}
      </aside>
      <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
        <Vue />
      </div>
    </div>
  )
}
